import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { partBomItemNoteService } from 'partBoms/services';

export const usePartBomItemNoteActions = ({
  notificationPosition = NotificationPosition.FORM,
}: {
  notificationPosition?: NotificationPosition;
} = {}) => ({
  ...useUpsertPartBomItemNote(),
  ...useRemovePartBomItemNotes({ notificationPosition }),
});

const useUpsertPartBomItemNote = () => {
  const { errors } = useHandleErrors({
    success: ({ partBomItemNote }) =>
      partBomItemNote.id
        ? { message: t('messagePartBomItemNoteUpdateSuccess') }
        : { message: t('messagePartBomItemNoteCreateSuccess') },
    loading: ({ partBomItemNote }) =>
      partBomItemNote.id
        ? { message: t('messagePartBomItemNoteUpdateInProgress') }
        : { message: t('messagePartBomItemNoteCreateInProgress') },
    error: ({ partBomItemNote }) =>
      partBomItemNote.id
        ? { title: t('errorTitlePartBomItemNoteUpdate') }
        : { title: t('errorTitlePartBomItemNoteCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation(partBomItemNoteService.upsertPartBomItemNote, {
    ...errors,
  });

  return { upsertPartBomItemNote: mutateAsync };
};

const useRemovePartBomItemNotes = ({
  notificationPosition = NotificationPosition.FORM,
}: {
  notificationPosition?: NotificationPosition;
}) => {
  const { errors } = useHandleErrors({
    success: ({ ids }) => ({
      message: t('messagePartBomItemNotesRemoveSuccess', { count: ids.length }),
    }),
    loading: ({ ids }) => ({
      message: t('messagePartBomItemNotesRemoveInProgress', { count: ids.length }),
    }),
    error: ({ ids }) => ({
      title: t('errorTitlePartBomItemNotesRemove', { count: ids.length }),
    }),
    position: notificationPosition,
  });

  const { mutateAsync } = useMutation(partBomItemNoteService.removePartBomItemNotes, {
    ...errors,
  });

  return { removePartBomItemNotes: mutateAsync };
};
